import { useParams, Link } from "react-router-dom";
import { MapPin, Check, ArrowUpRight, ArrowLeft, Wifi, ChefHat, WashingMachine, Car, BedDouble, Bath } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { UNITS } from "@/data/units";
import { usePublishedListings, usePhotoOverrides } from "@/data/useListings";
import { PhotoGallery } from "@/components/PhotoGallery";

const InfoTile = ({ icon: Icon, title, copy }) => (
  <div className="flex gap-4 p-6 rounded-2xl border border-[#E7E5E4] bg-white">
    <Icon className="w-5 h-5 text-[#8A9A86] shrink-0 mt-1" strokeWidth={1.5} />
    <div>
      <p className="font-medium text-[#1C1917]">{title}</p>
      <p className="text-sm text-[#44403C] font-light mt-1">{copy}</p>
    </div>
  </div>
);

export default function PropertyDetail() {
  const { slug } = useParams();
  const extra = usePublishedListings();
  const overrides = usePhotoOverrides();
  const unit = UNITS.find((u) => u.slug === slug) || (extra || []).find((u) => u.slug === slug);

  if (!unit && extra === null) return <section className="pt-40 pb-32" data-testid="unit-loading" />;

  if (!unit) {
    return (
      <section className="px-6 md:px-12 pt-40 pb-32 max-w-3xl mx-auto text-center" data-testid="unit-not-found">
        <h1 className="text-4xl font-light tracking-tight text-[#1C1917]">Unit not found</h1>
        <Link to="/properties" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-8 py-3.5 text-sm font-medium">
          Back to units
        </Link>
      </section>
    );
  }

  const isPrivate = unit.bathroom.toLowerCase().includes("private");
  // Admin-uploaded photos (Management Portal → Listings) take precedence over the original static gallery.
  // Only treat Management Portal uploads as "admin photos". The static unit.photos field holds raw UUIDs that are
  // transformed into Airbnb CDN URLs for `unit.gallery` — they are NOT usable as <img> src values.
  const adminPhotos = overrides[slug]?.photos?.length ? overrides[slug].photos : (unit.fromDb && unit.photos?.length ? unit.photos : null);
  const heroPhotos = adminPhotos || unit.gallery.map((url) => ({ url, caption: "" }));

  return (
    <article data-testid="unit-detail-page" className="pt-28 md:pt-32">
      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        <Link data-testid="back-to-properties" to="/properties" className="inline-flex items-center gap-2 text-sm text-[#44403C] hover:text-[#1C1917] transition-colors duration-300">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All units
        </Link>

        <Reveal className="mt-8">
          <div className="flex items-center gap-2 text-[#8A9A86] mb-4">
            <MapPin className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-xs tracking-[0.15em] uppercase">{unit.area}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-none text-[#1C1917]">{unit.name}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-[#44403C]">
            <span className="flex items-center gap-2"><BedDouble className="w-5 h-5 text-[#8A9A86]" strokeWidth={1.5} /> {unit.bed}</span>
            <span className="flex items-center gap-2"><Bath className="w-5 h-5 text-[#8A9A86]" strokeWidth={1.5} /> {unit.bathroom}</span>
            <span className="text-sm px-3 py-1 rounded-full bg-[#E8ECE7] text-[#4b5a48]">Private room in a shared home</span>
          </div>
        </Reveal>
      </div>

      {/* Hero gallery */}
      <div className="px-6 md:px-12 max-w-7xl mx-auto mt-12" data-testid="property-hero-gallery">
        <PhotoGallery photos={heroPhotos} alt={unit.name} hero testid="property-gallery" />
        {adminPhotos && <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#8A9A86]" data-testid="property-gallery-source">Photos by Masaya Hospitality</p>}
      </div>

      {/* Body */}
      <div className="px-6 md:px-12 max-w-7xl mx-auto mt-20 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-16">
        <div>
          <Reveal>
            <h2 className="text-2xl font-normal tracking-tight text-[#1C1917] mb-5">About this room</h2>
            <p className="text-lg text-[#44403C] leading-relaxed font-light">{unit.description}</p>
          </Reveal>

          <Reveal className="mt-14">
            <h2 className="text-2xl font-normal tracking-tight text-[#1C1917] mb-6">Amenities</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1">
              {unit.amenities.map((a) => (
                <div key={a} className="flex items-center gap-3 py-3 border-b border-[#E7E5E4]">
                  <Check className="w-4 h-4 text-[#8A9A86]" strokeWidth={2} />
                  <span className="text-[#1C1917] font-light">{a}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-14">
            <h2 className="text-2xl font-normal tracking-tight text-[#1C1917] mb-6">Kitchen, laundry, parking & Wi-Fi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoTile icon={ChefHat} title="Full kitchen" copy="Shared, fully equipped kitchen with cookware and appliances." />
              <InfoTile icon={WashingMachine} title="Laundry" copy="On-site laundry access included at no extra charge." />
              <InfoTile icon={Car} title="Free parking" copy="Free parking on premises for guests." />
              <InfoTile icon={Wifi} title="High-speed Wi-Fi" copy="Fast, reliable internet for remote work and streaming." />
            </div>
          </Reveal>

          <Reveal className="mt-14">
            <h2 className="text-2xl font-normal tracking-tight text-[#1C1917] mb-6">House rules</h2>
            <ul className="space-y-3">
              {unit.houseRules.map((rule) => (
                <li key={rule} className="flex items-center gap-3 text-[#44403C] font-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8A9A86] shrink-0" /> {rule}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="mt-14">
            <h2 className="text-2xl font-normal tracking-tight text-[#1C1917] mb-6">Nearby employers & destinations</h2>
            <div className="flex flex-wrap gap-3">
              {unit.nearby.map((n) => (
                <span key={n} className="text-sm px-4 py-2 rounded-full bg-[#F5F5F4] text-[#44403C] border border-[#E7E5E4]">{n}</span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Sticky booking card */}
        <div>
          <div className="lg:sticky lg:top-28 rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] p-8">
            <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Availability & booking</p>
            <p className="mt-4 text-[#44403C] font-light leading-relaxed">
              Check dates and reserve this room directly with Masaya Hospitality. For corporate or extended-stay
              arrangements, reach out to our team.
            </p>
            <Link
              data-testid="book-button"
              to="/book"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1C1917] text-white px-8 py-3.5 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm"
            >
              Request to Book <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
            <Link
              data-testid="longterm-button"
              to="/long-term-stays"
              className="mt-3 w-full inline-flex items-center justify-center rounded-full bg-[#8A9A86] text-white px-8 py-3.5 hover:bg-[#748570] transition-colors duration-300 font-medium text-sm"
            >
              Request Long-Term Availability
            </Link>
            <p className="mt-6 text-xs text-[#78716C] text-center">Utilities & Wi-Fi included · Flexible monthly stays</p>
          </div>
        </div>
      </div>

      <div className="h-24 md:h-32" />
    </article>
  );
}
